import React from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import { ArrowLeft, ShieldCheck, Envelope } from "@phosphor-icons/react";

const LAST_UPDATED = "9 February 2026";
const CONTACT_EMAIL = "altgasstove@gmail.com"; // TODO: swap to a curbcrew.co.za inbox once mail is set up
const COMPANY_NAME = "Curb Crew";
const SITE = "curbcrew.co.za";

export default function Privacy() {
  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <Link to="/" className="label-tag inline-flex items-center gap-2" data-testid="privacy-back">
            <ArrowLeft size={14} weight="bold" /> Back home
          </Link>
          <div className="mt-6 label-tag flex items-center gap-2"><ShieldCheck size={14} weight="bold" /> POPIA · Privacy Policy</div>
          <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tighter mt-2">
            Your data, your rules.
          </h1>
          <p className="mt-4 text-neutral-700 leading-relaxed">
            This policy explains exactly what {COMPANY_NAME} collects, why we collect it, who can see it, and how
            you can have it deleted. Written in plain English so you actually read it. Last updated <strong>{LAST_UPDATED}</strong>.
          </p>

          <article className="mt-10 bg-white border border-black p-6 sm:p-10 space-y-8 text-neutral-800 leading-relaxed">

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">1. Who we are</h2>
              <p className="mt-3">
                {COMPANY_NAME} ("we", "us") operates the smart business card platform at <strong>{SITE}</strong>.
                We are the <strong>responsible party</strong> for your personal information under the Protection
                of Personal Information Act (POPIA) of South Africa. You can reach us any time at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-semibold">{CONTACT_EMAIL}</a>.
              </p>
            </section>

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">2. What we collect</h2>
              <div className="mt-3 space-y-4">
                <div>
                  <div className="label-tag">If you are a cardholder</div>
                  <ul className="mt-2 list-disc pl-6 space-y-1">
                    <li>Your full name, skills, suburb / city, photo, short bio</li>
                    <li>Your email address and a hashed password (so you can log back in)</li>
                    <li>Your phone number — <strong>kept strictly private</strong>, never shown on the public profile</li>
                    <li>Optional media links (Website, Instagram, Facebook, TikTok, etc.) you choose to display</li>
                    <li>Your unique 8-character card code</li>
                  </ul>
                </div>
                <div>
                  <div className="label-tag">If you submit a lead (potential employer)</div>
                  <ul className="mt-2 list-disc pl-6 space-y-1">
                    <li>Your name, phone number, and the message you wrote to the cardholder</li>
                    <li>The card code you scanned</li>
                  </ul>
                </div>
                <div>
                  <div className="label-tag">If you simply scan a card</div>
                  <ul className="mt-2 list-disc pl-6 space-y-1">
                    <li>An anonymous scan record: city / region / country (derived from your IP), device type, and timestamp</li>
                    <li>We do <strong>not</strong> store your IP address in a way that links it back to you, and we never share scan locations publicly</li>
                  </ul>
                </div>
                <div>
                  <div className="label-tag">If you create an account</div>
                  <ul className="mt-2 list-disc pl-6 space-y-1">
                    <li>Your name, email, phone number, and a hashed password</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">3. Why we collect it</h2>
              <p className="mt-3">
                We use your data <strong>only</strong> for the following purposes:
              </p>
              <ul className="mt-2 list-disc pl-6 space-y-1">
                <li>To display your public cardholder profile when someone scans your QR code</li>
                <li>To relay lead messages to you privately, so a potential employer can reach you</li>
                <li>To let you log back in and edit your card</li>
                <li>To help us understand which cities our cards are most active in (anonymous, aggregated)</li>
                <li>To investigate and prevent fraud or abuse of the platform</li>
              </ul>
              <p className="mt-3">
                We <strong>do not</strong> sell your data, swap it with marketers, or use it to train AI models.
              </p>
            </section>

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">4. The privacy core: your phone number</h2>
              <div className="mt-3 bg-[color:var(--cc-yellow)] border border-black p-4">
                <p className="font-semibold">
                  Cardholder phone numbers are never displayed on the public profile.
                </p>
                <p className="mt-2 text-sm">
                  When a visitor scans your QR, they see your name, photo, skills and any media links you've chosen
                  to enable — and that's it. They submit their own phone number; we relay the lead to you. You decide
                  who you want to call back.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">5. Who we share data with</h2>
              <p className="mt-3">
                We use a small set of trusted service providers, strictly to operate the platform:
              </p>
              <ul className="mt-2 list-disc pl-6 space-y-1">
                <li><strong>Our hosting provider</strong> — private compute, database, and object storage used only to operate Curb Crew.</li>
                <li><strong>Resend</strong> — sending transactional emails (lead notifications, password resets).</li>
                <li><strong>ipapi.co</strong> — converting an IP address into an approximate city for the scan map. The IP is sent for lookup but is not stored on their side beyond their normal request logs.</li>
                <li><strong>OpenStreetMap</strong> — map tiles for the admin scan map (no personal data sent).</li>
              </ul>
              <p className="mt-3">
                We will never share your data with anyone else without your express consent — except where we are
                legally compelled to do so (e.g. by a valid court order).
              </p>
            </section>

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">6. How we keep it safe</h2>
              <ul className="mt-2 list-disc pl-6 space-y-1">
                <li>All traffic between your device and our servers is encrypted with HTTPS / TLS</li>
                <li>Your password is never stored in plain text — only a one-way bcrypt hash</li>
                <li>Database access is restricted to the minimum required services; no third party can query it directly</li>
                <li>Photos are stored in private object storage and served through authenticated URLs</li>
                <li>We log access events and review them for unusual activity</li>
              </ul>
              <p className="mt-3 text-sm text-neutral-600">
                No system is 100% secure. If we ever discover a breach that affects you, we'll tell you (and the
                Information Regulator) within a reasonable time, as POPIA requires.
              </p>
            </section>

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">7. How long we keep it</h2>
              <ul className="mt-2 list-disc pl-6 space-y-1">
                <li><strong>Cardholder profile:</strong> for as long as your card is active. You can delete it at any time by emailing us.</li>
                <li><strong>Leads:</strong> kept for 24 months so you can review them, then archived.</li>
                <li><strong>Anonymous scan events:</strong> rolling 12-month window for analytics, then deleted.</li>
                <li><strong>Lost-card reports:</strong> retained for fraud-prevention purposes for 24 months.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">8. Your rights under POPIA</h2>
              <p className="mt-3">You have the right to:</p>
              <ul className="mt-2 list-disc pl-6 space-y-1">
                <li><strong>Access</strong> the personal information we hold about you</li>
                <li><strong>Correct</strong> anything that's wrong (you can do most of this yourself in your dashboard)</li>
                <li><strong>Delete</strong> your account and have your data erased</li>
                <li><strong>Object</strong> to processing or withdraw consent at any time</li>
                <li><strong>Lodge a complaint</strong> with the Information Regulator if you think we've handled your data badly</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, email{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-semibold">{CONTACT_EMAIL}</a>.
                We'll respond within 14 working days.
              </p>
              <p className="mt-3 text-sm text-neutral-600">
                Information Regulator (South Africa): <a href="https://inforegulator.org.za" className="underline" target="_blank" rel="noreferrer">inforegulator.org.za</a>
              </p>
            </section>

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">9. Cookies & tracking</h2>
              <p className="mt-3">
                We keep tracking minimal:
              </p>
              <ul className="mt-2 list-disc pl-6 space-y-1">
                <li>An <strong>authentication cookie</strong> that keeps you logged in (essential — no opt-out, but no tracking either)</li>
                <li><strong>No third-party advertising trackers.</strong> No Facebook Pixel. No Google Ads.</li>
                <li><strong>No "fingerprinting"</strong> of your browser</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">10. Children</h2>
              <p className="mt-3">
                Curb Crew is not intended for users under 18. If you become aware that a minor has activated a card,
                please email us and we'll remove the account immediately.
              </p>
            </section>

            <section>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">11. Changes to this policy</h2>
              <p className="mt-3">
                If we change anything material, we'll update the "last updated" date at the top of this page and,
                where appropriate, notify you by email.
              </p>
            </section>

            <section className="border-t border-black pt-6">
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">Contact</h2>
              <p className="mt-3">
                Questions, complaints, deletion requests — all welcome. Email{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-semibold inline-flex items-center gap-1">
                  <Envelope size={14} weight="bold" /> {CONTACT_EMAIL}
                </a>.
              </p>
            </section>
          </article>

          <div className="mt-8 text-xs text-neutral-500" data-testid="privacy-footer">
            Curb Crew is a digital business card platform for South African service providers. We are not a recruitment
            agency and do not place workers, take commission, or arrange employment.
          </div>
        </div>
      </section>
    </div>
  );
}
